import torah from '../../data/torah.json';
import type { Pasuk } from '../../data/psukim';
import { getRabbiAlignmentJsonUrl, getRabbiAudioUrl } from '../../app/components/utils';
import { numberToHebrewLetters } from '../../app/utils/hebrewNumbers';
import { getTanachBookHebrew } from '../../admin/utils/tanachBookNameMap';
import type { PasukRef, ProgressStatus } from '../types';

const TORAH = torah as Pasuk[];

/** The 5 Torah books in order, keys as used in torah.json. */
export const TORAH_BOOKS = ['Bereshit', 'Shemot', 'Vayikra', 'Bamidbar', 'Devarim'] as const;

export const DEFAULT_NUSACH = 'ashkenaz';
export const DEFAULT_RABBI_KEY = 'michael_example_com';

/** Must match pasukKey() in serverless/functions/src/schoolService.ts */
export function pasukKey(p: PasukRef): string {
  return `${p.book}_${p.chapter}_${p.pasuk}`;
}

export function findPasuk(p: PasukRef | null | undefined): Pasuk | undefined {
  if (!p) return undefined;
  return TORAH.find((v) => v.book === p.book && v.chapter === p.chapter && v.pasuk === p.pasuk);
}

export function getChapterCount(book: string): number {
  return TORAH.reduce((max, v) => (v.book === book && v.chapter > max ? v.chapter : max), 0);
}

export function getPasukCount(book: string, chapter: number): number {
  return TORAH.reduce(
    (max, v) => (v.book === book && v.chapter === chapter && v.pasuk > max ? v.pasuk : max),
    0,
  );
}

const RABBI_CDN = 'https://d1q1chclchjxl4.cloudfront.net';

/**
 * Load rabbi files same-origin (/audio_rabi/...) so fetch() of the alignment JSON
 * and waveform decoding aren't blocked by CORS. Served by the /audio_rabi/*
 * behavior on the school.koltorah.app CloudFront in production, and by
 * src/setupProxy.js on the local dev server.
 */
function sameOrigin(url: string): string {
  return url.replace(RABBI_CDN, '');
}

export function getRabbiUrlForPasuk(
  p: PasukRef,
  nusach: string = DEFAULT_NUSACH,
  rabbiKey: string = DEFAULT_RABBI_KEY,
): string {
  const pasuk = findPasuk(p);
  return pasuk ? sameOrigin(getRabbiAudioUrl(pasuk, nusach, rabbiKey)) : '';
}

/** Word timings for the rabbi recording (same JSON PracticePage uses for karaoke). */
export function getRabbiAlignmentUrlForPasuk(
  p: PasukRef,
  nusach: string = DEFAULT_NUSACH,
  rabbiKey: string = DEFAULT_RABBI_KEY,
): string {
  const pasuk = findPasuk(p);
  return pasuk ? sameOrigin(getRabbiAlignmentJsonUrl(pasuk, nusach, rabbiKey)) : '';
}

/**
 * Splits pasuk text into words the same way PracticePage counts them for the
 * alignment (maqaf separates words), keeping the maqaf for display.
 */
export function splitPasukWords(hebrew: string): { word: string; joiner: string }[] {
  const parts = hebrew.trim().split(/(\s+|־)/);
  const words: { word: string; joiner: string }[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    if (parts[i]) words.push({ word: parts[i], joiner: parts[i + 1] === '־' ? '־' : ' ' });
  }
  return words;
}

/** Score thresholds for the status chips (score is 0-100). */
export function statusFromScore(bestScore: number | undefined, attempts = 0): ProgressStatus {
  if (!attempts || bestScore === undefined) return 'not_started';
  if (bestScore >= 85) return 'excellent';
  if (bestScore >= 60) return 'good';
  return 'in_progress';
}

export function isHebrew(lang: string): boolean {
  return lang.startsWith('he') || lang.startsWith('iw');
}

export function formatBookName(book: string, lang: string): string {
  return isHebrew(lang) ? getTanachBookHebrew(book) : book;
}

/** "בראשית א׳, א׳" in Hebrew, "Bereshit 1:1" otherwise. */
export function formatPasukRef(p: PasukRef, lang: string): string {
  if (isHebrew(lang)) {
    return `${formatBookName(p.book, lang)} ${numberToHebrewLetters(p.chapter)}, ${numberToHebrewLetters(p.pasuk)}`;
  }
  return `${p.book} ${p.chapter}:${p.pasuk}`;
}
